import React, {FC, forwardRef, useMemo} from 'react';

import {cn} from '@bem-react/classname';
import {RadioGroup, RadioGroupOption} from '@gravity-ui/uikit';

import OptionContent from '../OptionContent';
import QuestionLayout, {QuestionLayoutType} from '../QuestionLayout';
import Stars from '../Stars';

import {radioQuestionPositions} from './RadioQuestion.constants';
import {RadioQuestionProps, RadioQuestionView} from './RadioQuestion.types';
import {getCardSize} from './RadioQuestion.utils';

import './RadioQuestion.scss';

const cnRadioQuestion = cn('RadioQuestion');

const RadioQuestion: FC<RadioQuestionProps> = forwardRef<HTMLDivElement, RadioQuestionProps>(
    function RadioQuestion(
        {
            size = 'l',
            direction = 'vertical',
            items,
            view = RadioQuestionView.Default,
            cardSize,
            ...props
        },
        ref,
    ) {
        const options = useMemo(
            () =>
                items.map<RadioGroupOption>(({id, image, ...item}) => ({
                    value: id,
                    content: (
                        <OptionContent
                            key={id}
                            image={view === RadioQuestionView.Stars ? undefined : image?.links}
                            {...item}
                        />
                    ),
                    label: item.label,
                })),
            [items],
        );

        const withImage = useMemo(() => items.some((item) => item.image), [items]);

        return (
            <QuestionLayout
                {...props}
                type={QuestionLayoutType.Column}
                className={cnRadioQuestion(
                    {withImage, size: cardSize ?? getCardSize(view, items.length)},
                    [props.className],
                )}
                positions={radioQuestionPositions}
                control={
                    view === RadioQuestionView.Stars ? (
                        <Stars
                            {...props}
                            size={size}
                            options={options}
                            className={cnRadioQuestion('Control')}
                            optionClassName={cnRadioQuestion('Option')}
                            defaultValue={''}
                            name={`${props.id}-label`}
                        />
                    ) : (
                        <RadioGroup
                            {...props}
                            ref={ref}
                            size={size}
                            direction={direction}
                            options={options}
                            className={cnRadioQuestion('Control')}
                            optionClassName={cnRadioQuestion('Option')}
                            defaultValue={''}
                            aria-labelledby={`${props.id}-label`}
                        />
                    )
                }
            />
        );
    },
);
export default RadioQuestion;
